// src/components/RoomsInventory.jsx
import React, { useEffect, useRef, useState, useCallback } from "react";
import moment from "moment";
import { BsPencilSquare } from "react-icons/bs";
import { FaSpinner } from "react-icons/fa";
import { TabulatorFull as Tabulator } from "tabulator-tables";

import {
  getAvailableRooms,
  getContents,
  addContent,
} from "../../../FIREBASECONFIG"; // adjust path

import "bootstrap/dist/css/bootstrap.min.css";
import "tabulator-tables/dist/css/tabulator_bootstrap5.min.css";

const RoomsInventory = ({ hotelId }) => {
  const tableRef = useRef(null);
  const tabulatorRef = useRef(null);

  const [loading, setLoading] = useState(true);
  const [roomsDataSet, setRoomsDataSet] = useState([]);
  const [days, setDays] = useState([]);

  // Filters
  const [filterStartDate, setFilterStartDate] = useState(
    moment().format("YYYY-MM-DD")
  );
  const [filterEndDate, setFilterEndDate] = useState(
    moment().add(29, "days").format("YYYY-MM-DD")
  );

  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [modalData, setModalData] = useState({
    roomId: "",
    planId: "",
    planName: "",
    roomName: "",
  });
  const [price, setPrice] = useState("");
  const [saving, setSaving] = useState(false);

  // Update days array whenever filters change
  useEffect(() => {
    const start = moment(filterStartDate);
    const end = moment(filterEndDate);
    const daysCount = end.diff(start, "days") + 1;
    const arr = [];
    for (let i = 0; i < daysCount; i++) {
      arr.push(start.clone().add(i, "days").format("YYYY-MM-DD"));
    }
    setDays(arr);
  }, [filterStartDate, filterEndDate]);

  const fetchData = useCallback(async () => {
    if (!hotelId) return;
    setLoading(true);
    try {
      const allReservations = await getContents("reservations");
      const hotelReservations = allReservations.filter(
        (r) =>
          r.hotelId === hotelId &&
          (r.status || "").toLowerCase() !== "cancelled (hide)" &&
          (r.status || "").toLowerCase() !== "no-show"
      );

      const availableRooms = await getAvailableRooms(
        hotelId,
        filterStartDate,
        filterEndDate
      );

      const dataset = [];

      for (const room of availableRooms) {
        if (!room.plans || room.plans.length === 0) continue;

        for (const plan of room.plans) {
          const row = {
            ...plan,
            roomId: room.id,
            roomName: room.name,
            name: plan.name || plan.planName || "",
            roomsAvailable: Number(plan.totalPlan || plan.roomsAvailable || 0),
          };

          days.forEach((d, idx) => {
            const soldCount = hotelReservations.reduce((acc, res) => {
              const checkIn = moment(res.checkIn, "YYYY-MM-DD");
              const checkOut = moment(res.checkOut, "YYYY-MM-DD");
              const dayMoment = moment(d, "YYYY-MM-DD");

              const inRange =
                dayMoment.isSameOrAfter(checkIn, "day") &&
                dayMoment.isBefore(checkOut, "day");

              if (!inRange) return acc;

              const qty = (res.roomDetails || []).reduce((qacc, det) => {
                if (det.id === room.id && det.planId === plan.id) {
                  return qacc + Number(det.quantity || 0);
                }
                return qacc;
              }, 0);

              return acc + qty;
            }, 0);

            row[`day${idx + 1}`] = soldCount;
          });

          dataset.push(row);
        }
      }

      setRoomsDataSet(dataset);
    } catch (err) {
      console.error("RoomsInventory load error:", err);
    } finally {
      setLoading(false);
    }
  }, [hotelId, days, filterStartDate, filterEndDate]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Tabulator
  useEffect(() => {
    if (!tableRef.current) return;

    if (tabulatorRef.current) {
      try {
        tabulatorRef.current.destroy();
      } catch (e) {}
      tabulatorRef.current = null;
    }

    const columns = [
      {
        title: "Actions",
        hozAlign: "center",
        headerSort: false,
        formatter: () => `<i class="bi bi-pencil-square table-icon"></i>`,
        cellClick: (e, cell) => {
          const data = cell.getRow().getData();
          setModalData({
            roomId: data.roomId,
            planId: data.id,
            planName: data.name || data.planName || "",
            roomName: data.roomName,
          });
          setPrice("");
          setShowModal(true);
        },
      },
      {
        title: "Room Type",
        field: "roomName",
        headerSort: false,
        formatter: (cell) => {
          const d = cell.getRow().getData();
          return `${d.roomName}<br/><small>${d.name || ""}</small>`;
        },
      },
      ...days.map((day, idx) => ({
        title: moment(day).format("DD MMM YYYY"),
        field: `day${idx + 1}`,
        headerSort: false,
        formatter: (cell) => {
          const d = cell.getRow().getData();
          const total = Number(d.roomsAvailable || 0);
          const sold = Number(d[`day${idx + 1}`] || 0);
          const available = Math.max(total - sold, 0);

          // 🔥 Get rate from nightlyPrices array for this specific day
          const priceObj = (d.nightlyPrices || []).find((p) => p.date === day);
          const ratePerNight = priceObj ? priceObj.price : 0;

          return `
    Total: ${total} &nbsp; <br/>Sold: ${sold} <br/>
    Available: ${available} <br/>
    Rate: ${ratePerNight}
  `;
        },
      })),
    ];

    tabulatorRef.current = new Tabulator(tableRef.current, {
      layout: "fitColumns",
      data: roomsDataSet,
      groupBy: "roomName",
      groupHeader: (value, count) =>
        `${value} <span style='margin-left:10px;'>(${count} Rate Plans)</span>`,
      columns,
      pagination: "local",
      paginationSize: 100,
      placeholder: "No room plans found",
    });

    return () => {
      if (tabulatorRef.current) {
        try {
          tabulatorRef.current.destroy();
        } catch (e) {}
        tabulatorRef.current = null;
      }
    };
  }, [roomsDataSet, days]);

  const saveInventory = async () => {
    if (!modalData.planId || !modalData.roomId) return;
    if (!price) {
      alert("Please provide a price.");
      return;
    }
    setSaving(true);

    const payload = {
      hotel_id: hotelId,
      room_id: modalData.roomId,
      rateplan_id: modalData.planId,
      price: Number(price),
      startDate: filterStartDate,
      endDate: filterEndDate,
      priority: new Date().toISOString(),
      active: true,
    };

    try {
      await addContent("inventory", "", payload, null);
      setShowModal(false);
      await fetchData();
    } catch (err) {
      console.error("Failed to add inventory:", err);
      alert("Failed to save inventory.");
    } finally {
      setSaving(false);
    }
  };

  // Previous / Next month buttons
  const handlePrevMonth = () => {
    const newStart = moment(filterStartDate).subtract(1, "month");
    const newEnd = moment(newStart).add(29, "days");
    setFilterStartDate(newStart.format("YYYY-MM-DD"));
    setFilterEndDate(newEnd.format("YYYY-MM-DD"));
  };

  const handleNextMonth = () => {
    const newStart = moment(filterStartDate).add(1, "month");
    const newEnd = moment(newStart).add(29, "days");
    setFilterStartDate(newStart.format("YYYY-MM-DD"));
    setFilterEndDate(newEnd.format("YYYY-MM-DD"));
  };

  return (
    <div style={{ position: "relative" }}>
      {loading && (
        <div
          style={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: "rgba(255,255,255,0.85)",
            zIndex: 50,
          }}
        >
          <div style={{ textAlign: "center" }}>
            <FaSpinner
              className="spin"
              style={{ fontSize: 28, marginBottom: 8 }}
            />
            <div>Loading inventory...</div>
          </div>
        </div>
      )}

      {/* Filters Row */}
      <div className="d-flex mb-3 gap-2 align-items-end justify-content-between">
        <div className="d-flex gap-2">
          <div>
            <label className="form-label mb-0">Start Date</label>
            <input
              type="date"
              className="form-control"
              value={filterStartDate}
              onChange={(e) => setFilterStartDate(e.target.value)}
            />
          </div>

          <div>
            <label className="form-label mb-0">End Date</label>
            <input
              type="date"
              className="form-control"
              value={filterEndDate}
              onChange={(e) => setFilterEndDate(e.target.value)}
            />
          </div>
        </div>

        <div className="d-flex gap-2">
          <button className="btn btn-outline-primary" onClick={handlePrevMonth}>
            &lt; Previous Month
          </button>
          <button className="btn btn-outline-primary" onClick={handleNextMonth}>
            Next Month &gt;
          </button>
        </div>
      </div>

      <div id="example-table2" ref={tableRef} style={{ minHeight: 300 }} />

      {/* Modal */}
      {showModal && (
        <>
          <div
            className="modal fade show"
            tabIndex="-1"
            style={{ display: "block", background: "rgba(0,0,0,0.4)" }}
            role="dialog"
            aria-modal="true"
          >
            <div
              className="modal-dialog modal-md modal-dialog-centered"
              role="document"
            >
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Add Inventory</h5>
                  <button
                    type="button"
                    className="btn-close"
                    aria-label="Close"
                    onClick={() => setShowModal(false)}
                  />
                </div>
                <div className="modal-body">
                  <div className="mb-2">
                    <strong>Hotel:</strong> {hotelId}
                  </div>
                  <div className="mb-2">
                    <strong>Room:</strong> {modalData.roomName}
                  </div>
                  <div className="mb-2">
                    <strong>Plan:</strong> {modalData.planName}
                  </div>

                  <div className="mb-3">
                    <label className="form-label">Price</label>
                    <input
                      type="number"
                      className="form-control"
                      value={price}
                      onChange={(e) => setPrice(e.target.value)}
                      placeholder="Enter price per night"
                    />
                  </div>
                </div>

                <div className="modal-footer">
                  <button
                    className="btn btn-secondary"
                    onClick={() => setShowModal(false)}
                  >
                    Close
                  </button>
                  <button
                    className="btn btn-primary"
                    onClick={saveInventory}
                    disabled={saving}
                  >
                    {saving ? "Saving..." : "Save Inventory"}
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className="modal-backdrop fade show" style={{ zIndex: 1040 }} />
        </>
      )}
    </div>
  );
};

export default RoomsInventory;
